import FinancialTracking from "./FinancialTracking";
import MessageContent from "~/components/MessageContent";
import React, { createContext, useEffect, useRef, useState } from "react";
import Grid from "~/components/Grid";
import Filters from "~/components/Filters";
import { toast } from "react-toastify";
import { Section, buttonClass, primaryButtonClass, errorText, labelChannel, dateLabel } from "./shared";
import { invoiceRuleLabel } from './FinancialRule';

const DeliveryContext = createContext({});
const headers = [
  { title: "Student", name: "student", type: "text", filter: false },
  { title: "Channel", name: "channel", type: "text", filter: false },
  { title: "Destination", name: "destination", type: "text", filter: false },
  { title: "Result", name: "status", type: "text", filter: false },
  { title: "Invoice", name: "invoice", type: "text", filter: false },
  { title: "Invoice status", name: "invoice_status", type: "text", filter: false },
  { title: "Invoice balance", name: "invoice_balance", type: "text", filter: false },
  { title: "App read", name: "app_read", type: "text", filter: false },
  { title: "Details", name: "details", type: "text", filter: false },
];
const statuses = { pending: "Pending", processing: "Sending", accepted: "Accepted by provider", failed: "Failed", unknown: "Unknown outcome, do not retry", blocked: "Blocked" };

export default function CampaignSend({ id, status, dirty, canSend, request, filial, onBusy, onConfirmed, view = "sending" }) {
  const [review, setReview] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmedCount, setConfirmedCount] = useState("");
  const inFlight = useRef(false);
  const alive = useRef(true);
  const revision = useRef(0);
  const root = "/messaging/campaigns/" + id;

  const checkScope = data => {
    if (Number(data.filial_id) !== Number(filial.id)) throw new Error("Location changed. Reopen this campaign.");
    return data;
  };

  useEffect(() => {
    alive.current = true;
    const abort = new AbortController();
    const currentRevision = revision.current;
    request("get", root + "/deliveries", undefined, abort.signal)
      .then(({ data }) => { if (!abort.signal.aborted && revision.current === currentRevision) setReport(checkScope(data)); })
      .catch(failure => { if (!abort.signal.aborted && revision.current === currentRevision) setError(errorText(failure)); });
    return () => { alive.current = false; abort.abort(); };
  }, [id, filial.id, request]);

  useEffect(() => { if (error) toast.error(error); }, [error]);

  useEffect(() => { if (dirty) { setReview(null); setConfirmedCount(""); } }, [dirty]);

  const executionId = report?.execution?.id;
  const complete = report?.progress?.complete === true;
  const followInvoices = report?.financial_tracking?.available === true;
  useEffect(() => {
    if ((!executionId && status !== "confirmed") || complete) return undefined;
    const abort = new AbortController();
    let timer;
    async function poll() {
      if (abort.signal.aborted) return;
      if (!inFlight.current) {
        const currentRevision = revision.current;
        try {
          const { data } = await request("get", root + "/deliveries", undefined, abort.signal);
          if (!abort.signal.aborted && currentRevision === revision.current) {
            setReport(checkScope(data));
            setError("");
          }
        } catch (failure) {
          if (!abort.signal.aborted) setError("Unable to refresh progress. Background sending continues; the screen will try again automatically.");
        }
      }
      if (!abort.signal.aborted) timer = setTimeout(poll, 3000);
    }
    timer = setTimeout(poll, 3000);
    return () => { abort.abort(); clearTimeout(timer); };
  }, [executionId, complete, status, id, filial.id, request]);

  async function act(action) {
    const controlling = ["pause", "resume", "stop"].includes(action);
    if (inFlight.current || (action !== "deliveries" && (!canSend || (!controlling && dirty)))) return;
    if (action === "stop" && !window.confirm("Stop this campaign permanently? Pending messages will not be sent. Messages already being sent may still complete.")) return;
    if (action === "send" && (!review?.enabled || review.send_allowed === false || confirmedCount !== String(review.student_count))) return;
    inFlight.current = true;
    revision.current += 1;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      const { data } = await request(action === "deliveries" ? "get" : "post", root + "/" + action,
        action === "send" ? { review_token: review?.review_token } : action === "deliveries" ? undefined : {});
      if (!alive.current) return;
      checkScope(data);
      if (action === "send-preview") { setConfirmedCount(""); setReview(data); }
      else {
        setReport(data);
        setReview(null);
        if (data.execution) onConfirmed();
      }
    } catch (failure) {
      if (!alive.current) return;
      setReview(null);
      setError(errorText(failure));
      if (action === "send" || controlling) {
        try {
          const { data } = await request("get", root + "/deliveries");
          if (alive.current) {
            setReport(checkScope(data));
            if (data.execution) onConfirmed();
          }
        } catch {
          if (alive.current) setError("The sending status could not be refreshed. Reopen the campaign and check its recorded results before continuing.");
        }
      }
    } finally {
      inFlight.current = false;
      if (alive.current) { setBusy(false); onBusy(false); }
    }
  }

  const rows = report?.execution ? report.rows : review?.recipients || [];
  const gridData = rows.map((row, index) => ({ id: row.id || index, show: true, selectable: false, fields: [row.student_name, labelChannel(row.channel), row.destination, row.channel === "sms" && row.status === "accepted" ? "Accepted by Voxtell — delivery unconfirmed" : row.status ? statuses[row.status] : "Ready for confirmation", row.related_invoices?.length ? row.related_invoices.map(invoice => "#" + invoice.invoice_number).join(", ") : row.invoice ? "#" + row.invoice.invoice_number : "Not linked", row.invoice_tracking ? row.invoice_tracking.available ? row.invoice_tracking.status_now : "Unavailable" : "—", row.invoice_tracking?.balance_now != null ? "$ " + Number(row.invoice_tracking.balance_now).toFixed(2) : "—", row.channel === "push" ? row.app_read_at ? "Read " + dateLabel(row.app_read_at) : "No read recorded" : "Not available", row.error_message || row.error_code || (row.provider_id ? "Provider reference: " + row.provider_id : "")] }));

  const audienceLabel = review?.audience_filters?.mode === "branch" ? "All matching students in this location" : review?.audience_filters?.mode === "group" ? "Whole Student Group" : "Specific students";
  const countChannel = (channel) => review?.recipients?.filter(row => row.channel === channel && row.status !== "blocked").length || 0;
  const processingStatus = report?.execution?.processing_status || "running";
  const progressLabel = report?.progress?.state === "stopped" ? "Campaign stopped"
    : report?.progress?.state === "paused" ? "Campaign paused"
    : report?.progress?.complete ? report.progress.state === "completed" ? "Campaign completed" : "Campaign completed with issues"
    : report?.progress?.state === "awaiting_background_activation" ? "Background sending needs activation"
    : report?.worker?.available ? report.worker.status === "paused" ? "Sending paused" : report.worker.status === "error" ? "Worker needs attention" : report.worker.status === "sending" ? "Sending in background" : "Queued for background sending"
    : "Waiting for campaign worker";
  const statusNotice = report?.progress?.complete || processingStatus !== "running" ? null
    : report?.progress?.state === "awaiting_background_activation" ? "Activation required. Contact support."
    : !report?.worker?.available ? "Sending unavailable. Pending messages are queued."
    : report.worker.error_message ? "Sending interrupted. Contact support." : null;

  if (view === "statistics") return <section aria-label="Campaign statistics" className="w-full min-w-0">
    <Section>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {report?.execution && report.financial_tracking?.enabled !== false && report.financial_tracking
        ? <FinancialTracking tracking={report.financial_tracking} category={report.execution.category} messageSummary={report.summary} messages={report.rows || []} busy={busy} onRefresh={() => act("deliveries")} />
        : <div className="flex flex-col items-start gap-3 text-sm text-gray-600"><p>{report?.execution ? "Invoice tracking was not enabled for this campaign." : "Statistics become available after the campaign is confirmed with invoice tracking enabled."}</p><button type="button" className={buttonClass} disabled={busy} onClick={() => act("deliveries")}>Refresh statistics</button></div>}
    </Section>
  </section>;

  return <section aria-label="Campaign sending" className="w-full min-w-0">
    <Section title={report?.execution || status === "confirmed" ? "Sending status" : "Review and send"}>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {!report?.execution && status === "draft" && canSend && dirty && <div><button type="submit" className={buttonClass} disabled={busy}>Save draft to continue</button></div>}
      {!report?.execution && status === "draft" && canSend && !dirty && !review && <div><button type="button" className={buttonClass} disabled={busy} onClick={() => act("send-preview")}>Review and activate campaign</button></div>}
      {review && !dirty && <div className="flex w-full min-w-0 flex-col gap-4">
        <div className="flex w-full flex-row flex-wrap gap-x-10 gap-y-3">
          <SummaryItem label="Campaign" value={review.campaign_name} />
          <SummaryItem label="Location" value={filial.name || filial.alias} />
          <SummaryItem label="Audience" value={audienceLabel} />
          <SummaryItem label="Population" value={review.audience_filters?.financial ? review.audience_filters.financial.student_status || "All student statuses" : review.audience_filters?.population === "alumni" ? "Ex-students (Inactive)" : "Active students (In Class)"} />
        </div>
        <p className="text-sm font-semibold">{review.student_count} students · {countChannel("email") + countChannel("push") + countChannel("sms")} attempts ({countChannel("email")} email, {countChannel("push")} mobile devices, {countChannel("sms")} SMS)</p>
        {review.sms_sender && <p className="text-sm">SMS from: <strong>{review.sms_sender.number}</strong> · Voxtell user {review.sms_sender.user}</p>}
        {review.recipients?.some(row => row.status === "blocked") && <div role="status" className="rounded border border-orange-200 bg-orange-50 p-3 text-sm">
          <p>The recipients listed below have blocked deliveries. Their reasons will be recorded; other recipients and channels will continue.</p>
          <ul>{review.recipients.filter(row => row.status === "blocked").map(row => <li key={row.student_id + row.channel}>{row.student_name} · {row.registration_number}: {row.error_message}</li>)}</ul>
        </div>}
        {review.financial_rule && <p className="text-sm">Invoice rule: <strong>{invoiceRuleLabel(review.financial_rule)}</strong> · Reference date {review.rule_date}</p>}
        {!!review.excluded?.length && <div className="rounded border border-orange-200 bg-orange-50 p-3 text-sm">
          <p>{review.excluded.length} of {review.audience_count} selected students excluded. They will not receive this message.</p>
          <ul className="mt-2 max-h-48 overflow-y-auto">{review.excluded.map(student => <li key={student.student_id}>{student.student_name} · {student.registration_number}: {student.reason}</li>)}</ul>
        </div>}
        {countChannel("push") > 0 && <p className="text-xs text-gray-600">Mobile notifications also save one personalized message per student in the app message center.</p>}
        {review.track_invoices && <div className="rounded border bg-orange-50 p-3 text-sm"><strong>Invoice tracking enabled: {review.tracked_invoices?.length || 0} invoices</strong><p>Every invoice matching Financial filters for these recipients will be tracked, with or without payment links.</p><details><summary className="cursor-pointer">Review tracked invoices</summary><ul className="max-h-48 overflow-auto">{review.tracked_invoices?.map(invoice => <li key={invoice.receivable_id}>Invoice #{invoice.invoice_number} · {invoice.initial_status} · Balance $ {Number(invoice.initial_balance).toFixed(2)}</li>)}</ul></details></div>}
        <RecipientReview key={review.review_token} review={review} />
        {!review.enabled && <p role="alert" className="text-sm text-red-700">Sending is disabled in this environment. You can review and type the confirmation, but sending remains blocked.</p>}
        {review.block_reason && <p role="alert" className="text-sm text-red-700">{review.block_reason}</p>}
        <label className="flex flex-col items-start text-xs">
          <span className="px-1">Type the number of students to confirm: {review.student_count}</span>
          <input type="text" inputMode="numeric" autoComplete="off" className="w-40 border rounded-xs p-2 text-sm outline-hidden focus:border-mila_orange" value={confirmedCount} onChange={event => setConfirmedCount(event.target.value.replace(/[^0-9]/g, ""))} disabled={busy} />
        </label>
        <div className="flex gap-2">
          <button type="button" className={primaryButtonClass} disabled={busy || !review.enabled || review.send_allowed === false || confirmedCount !== String(review.student_count)} onClick={() => act("send")}>Send to {review.student_count} students now</button>
          <button type="button" className={buttonClass} disabled={busy} onClick={() => setReview(null)}>Cancel</button>
        </div>
      </div>}
      {report?.execution && <>
        {report.execution.financial_rule && <p className="text-sm">Invoice rule: <strong>{invoiceRuleLabel(report.execution.financial_rule)}</strong> · Reference date {report.execution.rule_date}</p>}
        {!!report.execution.excluded?.length && <p className="text-sm">{report.execution.excluded.length} selected students were excluded because no invoice matched the rule.</p>}
        <div role="status" className="space-y-2 rounded border bg-slate-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <strong>{progressLabel}</strong>
            <strong className="text-xl">{Number(report.progress?.percent || 0).toFixed(1)}%</strong>
          </div>
          <progress aria-label="Campaign processing progress" className="h-3 w-full accent-orange-600" max="100" value={report.progress?.percent || 0} />
          <p className="text-sm">{report.progress?.processed || 0}/{report.progress?.total || 0} processed · {report.summary.processing || 0} sending · {report.summary.pending || 0} pending</p>
          <p className="text-sm">{report.execution.student_count} students · {report.summary.accepted || 0} accepted · {report.summary.failed || 0} failed · {report.summary.blocked || 0} blocked · {report.summary.unknown || 0} unknown</p>
          {statusNotice && <p role="alert" className="text-sm text-red-700">{statusNotice}</p>}
          {canSend && !report.progress?.complete && processingStatus !== "stopped" && <div className="flex flex-wrap gap-2">
            <button type="button" className={buttonClass} disabled={busy} onClick={() => act(processingStatus === "paused" ? "resume" : "pause")}>{processingStatus === "paused" ? "Resume campaign" : "Pause campaign"}</button>
            <button type="button" className={buttonClass} disabled={busy} onClick={() => act("stop")}>Stop campaign</button>
          </div>}
        </div>
      </>}
      {report?.execution && !!gridData.length && <DeliveryContext.Provider value={{ gridData, gridHeader: headers, opened: null, handleOpened: () => null, orderBy: null, setOrderBy: () => null }}><div className="flex h-80 w-full min-w-0 min-h-0 flex-col overflow-hidden"><Grid Context={DeliveryContext} handleEdit={false} /></div></DeliveryContext.Provider>}
    </Section>
  </section>;
}

function SummaryItem({ label, value }) {
  return <div className="flex min-w-0 flex-col text-sm"><span className="px-1 text-xs">{label}</span><strong className="px-1 break-words">{value}</strong></div>;
}

function RecipientReview({ review }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(null);
  const [selected, setSelected] = useState(null);
  const [orderBy, setOrderBy] = useState({ column: "student", asc: true });
  const limit = 20;
  const gridHeader = [
    { title: "Student", name: "student", type: "text", filter: false },
    { title: "Registration", name: "registration", type: "text", filter: false },
    { title: "Channel", name: "channel", type: "text", filter: false },
    { title: "Destination", name: "destination", type: "text", filter: false },
  ];
  const recipients = review.recipients || [];
  const matches = recipients.map((recipient, index) => ({ id: String(index), show: true, selectable: false, fields: [recipient.student_name, recipient.registration_number, labelChannel(recipient.channel), recipient.destination] }))
    .filter(row => row.fields.some(value => String(value || "").toLowerCase().includes((search?.value || "").trim().toLowerCase())))
    .sort((left, right) => {
      const column = gridHeader.findIndex(header => header.name === orderBy.column);
      return String(left.fields[column] || "").localeCompare(String(right.fields[column] || "")) * (orderBy.asc ? 1 : -1);
    });
  const pages = Math.max(1, Math.ceil(matches.length / limit));
  const gridData = matches.slice((page - 1) * limit, page * limit);
  const recipient = selected === null ? null : recipients[selected];
  const content = recipient?.content || review.contents?.find(item => item.channel === recipient?.channel);
  return <div className="flex w-full min-w-0 flex-col gap-3">
    <DeliveryContext.Provider value={{ gridData, gridHeader, gridDetails: { totalRows: matches.length }, page, setPage: value => setPage(Number(value)), pages, limit, activeFilters: [], search,
      handleFilters: ({ value }) => { setSearch({ value }); setPage(1); }, orderBy, setOrderBy: value => { setOrderBy(value); setPage(1); }, opened: null, handleOpened: setSelected }}>
      <Filters Context={DeliveryContext} access={{}} />
      <div className="flex h-72 w-full min-w-0 min-h-0 flex-col overflow-hidden"><Grid Context={DeliveryContext} horizontalScroll /></div>
    </DeliveryContext.Provider>
    {recipient && <div className="flex flex-col gap-2 rounded-md border bg-slate-50 p-3 text-sm">
      <div className="flex items-center justify-between gap-2"><p>{recipient.student_name} · {recipient.registration_number} · {recipient.destination}</p><button type="button" className={buttonClass} onClick={() => setSelected(null)}>Close</button></div>
      <strong>{labelChannel(recipient.channel)}{recipient.channel !== "sms" && " · " + (content?.subject || "")}</strong>
      {recipient.invoice && <p className="text-xs">Invoice #{recipient.invoice.invoice_number} · Due {recipient.invoice.due_date} · Amount still to pay {recipient.invoice.balance}</p>}
      <MessageContent body={content?.body} className="max-h-64 overflow-y-auto" />
    </div>}
  </div>;
}
